import React, { useState } from "react";
import type { CreatedInvitation, InvitationSummary } from "../../types";
import { resendInvitation, revokeInvitation } from "../../authApi";
import { formatDateMonthDay } from "../../../utils/format";
import { apiErrorMessage } from "../../../utils/apiError";
import "./InvitationsList.css";

interface Props {
  invitations: InvitationSummary[];
  onChanged: () => void;
  onResent: (created: CreatedInvitation) => void;
}

const InvitationsList: React.FC<Props> = ({
  invitations,
  onChanged,
  onResent,
}) => {
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleResend = async (id: number) => {
    setBusyId(id);
    setError(null);
    try {
      const created = await resendInvitation(id);
      onResent(created);
      onChanged();
    } catch (err: any) {
      setError(apiErrorMessage(err, "Failed to resend"));
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (id: number) => {
    setBusyId(id);
    setError(null);
    try {
      await revokeInvitation(id);
      onChanged();
    } catch (err: any) {
      setError(apiErrorMessage(err, "Failed to revoke"));
    } finally {
      setBusyId(null);
    }
  };

  if (invitations.length === 0) {
    return <p className="invites-empty">No invitations yet.</p>;
  }

  return (
    <>
      {error && <div className="invites-error">{error}</div>}
      <table className="admin-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Tenant</th>
            <th>Role</th>
            <th>Status</th>
            <th>Expires</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((inv) => {
            const isPending = inv.status === "pending";
            const canResend = inv.status === "pending" || inv.status === "expired";
            return (
              <tr key={inv.id}>
                <td>{inv.email}</td>
                <td>{inv.tenant_display_name || "—"}</td>
                <td>
                  {inv.is_platform_admin ? "Platform Admin" : inv.role}
                </td>
                <td>
                  <span className={`invite-badge invite-badge--${inv.status}`}>
                    {inv.status}
                  </span>
                </td>
                <td>{formatDateMonthDay(inv.expires_at) || "—"}</td>
                <td className="invite-actions">
                  {canResend && (
                    <button
                      className="invite-action"
                      disabled={busyId === inv.id}
                      onClick={() => handleResend(inv.id)}
                    >
                      Resend
                    </button>
                  )}
                  {isPending && (
                    <button
                      className="invite-action invite-action--danger"
                      disabled={busyId === inv.id}
                      onClick={() => handleRevoke(inv.id)}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export default InvitationsList;
